import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MenuProvider } from './contexts/MenuContext';
import { useAuthContext, AuthProvider } from './context/AuthContext';
import Sidebar from './components/Sidebar';
import MainLayout from './layouts/MainLayout';
import SignIn from './components/auth/SignIn';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import OnboardingLayout from './components/onboarding/OnboardingLayout';
import Analytics from './components/Analytics';
import DTCWebsites from './components/DTCWebsites';
import InboundShipments from './components/InboundShipments';
import InventoryManagement from './components/InventoryManagement';
import MarketplaceManagement from './components/MarketplaceManagement';
import Notifications from './components/Notifications';
import OrderManagement from './components/OrderManagement';
import Profitability from './components/Profitability';
import ThirdPartyLogistics from './components/ThirdPartyLogistics';
import UploadProduct from './components/UploadProduct';
import ComplianceAI from './components/strategy/ComplianceAI';
import Calendar from './components/strategy/Calendar';
import Campaign from './components/strategy/Campaign';
import CampaignWizard from './components/strategy/campaign/CampaignWizard';
import BusinessDNA from './components/strategy/BusinessDNA';
import StrategyDashboard from './components/strategy/StrategyDashboard';
import LogisticsDashboard from './components/logistics/LogisticsDashboard';
import MarketingDashboard from './components/marketing/MarketingDashboard';
import SalesAutomation from './components/marketing/SalesAutomation';
import OrdersDashboard from './components/orders/OrdersDashboard';
import Forecasting from './components/automation/Forecasting';
import MarketingCalendar from './components/automation/MarketingCalendar';
import ScenarioPlanning from './components/automation/ScenarioPlanning';
import Performance from './components/automation/Performance';
import Reports from './components/automation/Reports';
import UserOnboarding from './components/automation/UserOnboarding';
import DataAnalysis from './components/automation/DataAnalysis';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import CaseStudies from './pages/CaseStudies';
import Contact from './pages/Contact';
import Careers from './pages/Careers';
import SeniorSoftwareEngineer from './pages/positions/SeniorSoftwareEngineer';
import InternshipProgram from './pages/InternshipProgram';
import InternshipApply from './pages/InternshipApply';
import SoftwareEngineer from './pages/positions/SoftwareEngineer';
import GeneralInfo from './components/onboarding/GeneralInfo';
import NewMarkets from './components/onboarding/NewMarkets';
import ProductInformation from './components/onboarding/ProductInformation';
import ConnectChannels from './components/onboarding/ConnectChannels';
import UploadDocuments from './components/onboarding/UploadDocuments';
import DemandForecast from './components/onboarding/DemandForecast';
import CompleteProfile from './components/auth/CompleteProfile';
import { useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { onboardingApi } from './services/api/onboarding';
import { supabase } from './config/supabase';
import AuthCallback from './components/auth/AuthCallback';
import { Toaster } from 'react-hot-toast';
import LoadingSpinner from './components/common/LoadingSpinner';
import Privacy from './pages/Privacy';
import PositionDetails from './pages/positions/PositionDetails';
import JobApplicationForm from './pages/positions/JobApplicationForm';
import FrontendEngineer from './pages/positions/FrontendEngineer';
import UXUIDesigner from './pages/positions/UXUIDesigner';
import ProductOwner from './pages/positions/ProductOwner';
import FullStackEngineer from './pages/positions/FullStackEngineer';
import BackendEngineer from './pages/positions/BackendEngineer';
import TechnicalProjectManager from './pages/positions/TechnicalProjectManager';

function DashboardLayout({ children }) {
  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 overflow-auto p-8">
        {children}
      </div>
    </div>
  );
}

function App() {

  return (
    <>
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            theme: {
              primary: '#4aed88',
            },
          },
          error: {
            duration: 3000,
            theme: {
              primary: '#ff4b4b',
            },
          },
        }}
      />
      <AuthProvider>
        <MenuProvider>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout><Home /></MainLayout>} />
          <Route path="/about" element={<MainLayout><About /></MainLayout>} />
          <Route path="/services" element={<MainLayout><Services /></MainLayout>} />
          <Route path="/case-studies" element={<MainLayout><CaseStudies /></MainLayout>} />
          <Route path="/contact" element={<MainLayout><Contact /></MainLayout>} />
          <Route path="/careers" element={<MainLayout><Careers /></MainLayout>} />
          <Route path="/careers/:position" element={<MainLayout><PositionDetails /></MainLayout>} />
          <Route path="/careers/:positionSlug/apply" element={<MainLayout><JobApplicationForm /></MainLayout>} />
          <Route path="/careers/senior-software-engineer" element={<MainLayout><SeniorSoftwareEngineer /></MainLayout>} />
          <Route path="/internship" element={<MainLayout><InternshipProgram /></MainLayout>} />
          <Route path="/internship/apply" element={<MainLayout><InternshipApply /></MainLayout>} />
          <Route path="/internship/software-engineer" element={<MainLayout><SoftwareEngineer /></MainLayout>} />
          <Route path="/careers/full-stack-software-engineer" element={<MainLayout><FullStackEngineer /></MainLayout>} />
          <Route path="/careers/backend-software-engineer" element={<MainLayout><BackendEngineer /></MainLayout>} />
          <Route path="/careers/frontend-software-engineer" element={<MainLayout><FrontendEngineer /></MainLayout>} />
          <Route path="/careers/ux-ui-designer" element={<MainLayout><UXUIDesigner /></MainLayout>} />
          <Route path="/careers/product-owner" element={<MainLayout><ProductOwner /></MainLayout>} />
          <Route path="/careers/technical-project-manager" element={<MainLayout><TechnicalProjectManager /></MainLayout>} />

            {/* Auth Routes */}
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Callback Route for OAuth */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Profile Completion Route */}
            <Route path="/complete-profile" element={<CompleteProfile />} />

            {/* Protected Routes */}
            <Route element={<RequireAuth />}>
              {/* Onboarding Routes */}
              <Route path="/onboarding" element={<OnboardingLayout />}>
                {/* <Route path="company" element={<GeneralInfo />} /> */}
                <Route path="markets" element={<NewMarkets />} />
                <Route path="products" element={<ProductInformation />} />
                <Route path="channels" element={<ConnectChannels />} />
                <Route path="forecast" element={<DemandForecast />} />
                <Route path="documents" element={<UploadDocuments />} />
                <Route index element={<Navigate to="markets" replace />} />
              </Route>

              {/* Dashboard Routes */}
              <Route path="/analytics/dashboard" element={<DashboardLayout><Analytics /></DashboardLayout>} />
              <Route path="/dtc-websites" element={<DashboardLayout><DTCWebsites /></DashboardLayout>} />
              <Route path="/inbound" element={<DashboardLayout><InboundShipments /></DashboardLayout>} />
              <Route path="/inventory" element={<DashboardLayout><InventoryManagement /></DashboardLayout>} />
              <Route path="/marketplace" element={<DashboardLayout><MarketplaceManagement /></DashboardLayout>} />
              <Route path="/notifications" element={<DashboardLayout><Notifications /></DashboardLayout>} />
              <Route path="/orders" element={<DashboardLayout><OrderManagement /></DashboardLayout>} />
              <Route path="/profitability" element={<DashboardLayout><Profitability /></DashboardLayout>} />
              <Route path="/3pl" element={<DashboardLayout><ThirdPartyLogistics /></DashboardLayout>} />
              <Route path="/upload" element={<DashboardLayout><UploadProduct /></DashboardLayout>} />
              <Route path="/analytics" element={<DashboardLayout><Analytics /></DashboardLayout>} />
              {/* Strategy Routes */}
              <Route path="/strategy/compliance" element={<DashboardLayout><ComplianceAI /></DashboardLayout>} />
              <Route path="/strategy/calendar" element={<DashboardLayout><Calendar /></DashboardLayout>} />
              <Route path="/strategy/campaign" element={<DashboardLayout><Campaign /></DashboardLayout>} />
              <Route path="/strategy/campaign/new" element={<DashboardLayout><CampaignWizard /></DashboardLayout>} />
              <Route path="/strategy/business-dna" element={<DashboardLayout><BusinessDNA /></DashboardLayout>} />
              <Route path="/strategy/dashboard" element={<DashboardLayout><StrategyDashboard /></DashboardLayout>} />

              {/* Dashboard Home Routes */}
              <Route path="/logistics/dashboard" element={<DashboardLayout><LogisticsDashboard /></DashboardLayout>} />
              <Route path="/marketing/dashboard" element={<DashboardLayout><MarketingDashboard /></DashboardLayout>} />
              <Route path="/marketing/automation" element={<DashboardLayout><SalesAutomation /></DashboardLayout>} />
              <Route path="/orders/dashboard" element={<DashboardLayout><OrdersDashboard /></DashboardLayout>} />

              {/* Automation Routes */}
              <Route path="/automation/onboarding" element={<DashboardLayout><UserOnboarding /></DashboardLayout>} />
              <Route path="/automation/data" element={<DashboardLayout><DataAnalysis /></DashboardLayout>} />
              <Route path="/automation/forecast" element={<DashboardLayout><Forecasting /></DashboardLayout>} />
              <Route path="/automation/calendar" element={<DashboardLayout><MarketingCalendar /></DashboardLayout>} />
              <Route path="/automation/scenarios" element={<DashboardLayout><ScenarioPlanning /></DashboardLayout>} />
              <Route path="/automation/performance" element={<DashboardLayout><Performance /></DashboardLayout>} />
              <Route path="/automation/reports" element={<DashboardLayout><Reports /></DashboardLayout>} />
            </Route>
            <Route path="/privacy" element={<Privacy />} />
          </Routes>
        </MenuProvider>
      </AuthProvider>
    </>
  );
}

// Auth protection component
function RequireAuth() {
  const { user, loading } = useAuthContext();
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        // 1. Get user profile using /me endpoint
        const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        const data = await response.json();
        console.log('User data:', data);

        const isProfileIncomplete = !data.user || 
          !data.user.phone_number || 
          !data.user.country || 
          !data.user.company_name || 
          !data.user.business_website;

        // If profile is incomplete and not on complete-profile page, redirect
        if (isProfileIncomplete && location.pathname !== '/complete-profile') {
          toast.error('Please complete your profile first');
          navigate('/complete-profile', { replace: true });
          return;
        }

        // 2. If profile is complete, check onboarding status
        if (!isProfileIncomplete) {
          try {
            const response = await onboardingApi.getStatus();
            console.log('Onboarding status:', response);
            // If onboarding is not completed and user is not already on onboarding page
            if (response.status !== 'completed' && !location.pathname.startsWith('/onboarding')) {
              toast.error('Please complete onboarding first');
              navigate('/onboarding', { replace: true });
            }
            // If onboarding is completed and user tries to access onboarding pages
            else if (response.status === 'completed' && location.pathname.startsWith('/onboarding')) {
              toast.error('Onboarding already completed');
              navigate('/strategy/dashboard', { replace: true });
            }
          } catch (error) {
            console.error('Failed to check onboarding status:', error);
          }
        }
      } catch (error) {
        console.error('Failed to check user status:', error);
      }
    };

    if (user) {
      checkUserStatus();
    }
  }, [user, location.pathname, navigate]);

  if (loading) {
    return <LoadingSpinner message="Checking authentication..." />;
  }
  
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  
  return <Outlet />;
}

export default App;